'use client';

// Global
import type { SearchResultsStoreState } from '@sitecore-search/react';
import { WidgetDataType, widget } from '@sitecore-search/react';
import { useState, useRef, useEffect } from 'react';
import type { ComponentParams } from '@sitecore-content-sdk/nextjs';
import { tv } from 'tailwind-variants';
import {
  Context as CloudSearchContext,
  getWidgetData,
  SearchWidgetItem,
  ComparisonFilter,
  WidgetRequestData,
} from '@sitecore-cloudsdk/search/browser';

// Local
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import { useSearchInitialization } from 'lib/context/SearchInitializationContext';
import { getTestProps } from 'lib/testing/utils';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import useIsEditing from 'lib/hooks/useIsEditing';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { useSetIsDataAvailable } from 'lib/context/IsDataAvailableContext';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';

type NewsModel = {
  description: string;
  id: string;
  image_url: string;
  key_contact_id: string;
  news_type: string;
  source_id: string;
  type: string;
  url: string;
  subtitle: string;
  short_description: string;
};

type SearchResultsProps = {
  defaultItemsPerPage?: SearchResultsStoreState['itemsPerPage'];
  rfkId?: string;
  params?: ComponentParams;
  routeItemId?: string | null;
};

export const MyClientWithSearchComponent = ({
  defaultItemsPerPage = 4,
  rfkId,
  params,
  routeItemId,
}: SearchResultsProps) => {
  const articleCardsRef = useRef<HTMLDivElement>(null);

  const defaultSiteLocale =
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en-US';
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [news, setNews] = useState<NewsModel[]>([]);
  const isEditing = useIsEditing();
  const styles = parseStyleParams(params, ['cta1']);
  const { getDictionaryValue } = useDictionary();
  const readFullStoryTextValue = getDictionaryValue('ReadFullStory') || 'Read full story';
  const setIsDataAvailable = useSetIsDataAvailable();
  const { isSearchInitialized } = useSearchInitialization();

  useEffect(() => {
    if (!isSearchInitialized) {
      return;
    }

    const fetchNews = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const rfk = rfkId || 'rfkid_client_stories';

        // Build locale context
        const locale = defaultSiteLocale;
        const [language, countryRaw] = locale.split('-');
        const country = (countryRaw || 'US').toLowerCase();
        const ctx = new CloudSearchContext({
          locale: {
            language: (language || 'en').toLowerCase(),
            country,
          },
        });

        const searchSourceIds = process.env.NEXT_PUBLIC_SEARCH_NEWS_SOURCE_ID;

        // Define the filter to match key_contact_id with the route item ID
        const contactIdFilter = new ComparisonFilter('key_contact_id', 'eq', routeItemId);

        const widgetItem = new SearchWidgetItem(
          'newsarticle',
          rfk,
          {
            limit: defaultItemsPerPage || 4,
            content: {},
            filter: contactIdFilter,
          },
          searchSourceIds ? [searchSourceIds] : undefined
        );
        const response = await getWidgetData(new WidgetRequestData([widgetItem]), ctx);
        const content = response?.widgets?.[0]?.content as NewsModel[] | undefined;
        const available = !isEditing && (content?.length ?? 0) > 0;
        setIsDataAvailable(available);
        setNews(content || []);
      } catch {
        setError('Failed to load news');
      } finally {
        setIsLoading(false);
      }
    };

    // Only run client-side
    if (typeof window !== 'undefined') {
      fetchNews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfkId, defaultSiteLocale, defaultItemsPerPage, routeItemId, isSearchInitialized]);

  useScrollElementIntoView(articleCardsRef.current, {
    scrollTargetId: 'search-result-cards',
  });

  const { RenderingIdentifier } = params || {};

  const {
    base,
    card,
    container,
    header,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    heading: headingTV,
    subheading: subheadingTV,
    description: descriptionTV,
    sectionDivider,
    footer,
    cta,
    imageWrapper,
  } = TAILWIND_VARIANTS();

  if (!isEditing && !news?.length) {
    return <></>;
  }
  if (isEditing && !news?.length) {
    return <div className="p-spacing-spacing-16">My client data will come from search widget</div>;
  }

  return (
    <div id={RenderingIdentifier} {...getTestProps(`search-result-cards`)}>
      {isLoading && (
        <ul className={base()}>
          <li className={card()}>
            <span>Loading…</span>
          </li>
        </ul>
      )}
      {!isLoading && !error && news?.length > 0 && (
        <ul className={base()}>
          {news.map((n, idx) => (
            <li key={n?.id || idx} className={card()}>
              <article
                className={container()}
                data-component="authorable/shared/lists/carditem"
                {...editingHydrationProps(isEditing)}
                id={RenderingIdentifier}
                {...getTestProps(`card-item-${n?.id}`)}
              >
                <div className={header()}>
                  {n?.image_url ? (
                    <ImageWrapper
                      field={{
                        value: {
                          src: n?.image_url,
                          alt: n?.subtitle,
                          title: n?.subtitle,
                        },
                      }}
                      className={imageWrapper()}
                      {...getTestProps(`image`)}
                    />
                  ) : (
                      <ImageWrapper
                        field={{
                          value: {
                            src: '/assets/profile_placeholder.png',
                            alt: 'No image',
                          },
                        }}
                        className={imageWrapper()}
                        {...getTestProps(`related-profile-image-placeholder`)}
                      />
                  )}
                </div>
                <div className={body()}>
                  <div className={contentWrapper()}>
                    <div className={content()}>
                      <div className={headingWithEyebrow()}>
                        <PlainTextWrapper
                          className={headingTV()}
                          field={{
                            value: n?.subtitle,
                          }}
                          {...getTestProps(`heading`)}
                        />
                      </div>

                      <PlainTextWrapper
                        className={subheadingTV()}
                        field={{
                          value: n?.news_type,
                        }}
                        {...getTestProps(`sub-heading`)}
                      />
                      <PlainTextWrapper
                        className={descriptionTV()}
                        field={{
                          value: n?.short_description,
                        }}
                        {...getTestProps(`description`)}
                      />
                      {n?.url ? <span className={sectionDivider()}></span> : null}
                    </div>
                    <div className={footer()}>
                      <LinkWrapper
                        ctaComponentClass="component-card-item-button-1-color"
                        className={cta()}
                        ctaStyle={getCtaStyle(styles.cta1, 'primary')}
                        field={{
                          value: {
                            href: n?.url,
                            linktype: 'internal',
                            text: readFullStoryTextValue,
                          },
                        }}
                        ctaIcon="arrow-right"
                        {...getTestProps(`primary-cta`)}
                      />
                    </div>
                  </div>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
      {!isLoading && error && (
        <ul className={base()}>
          <li className={card()}>
            <span>{error}</span>
          </li>
        </ul>
      )}
    </div>
  );
};

const MyClientListSearchWidget = widget(
  MyClientWithSearchComponent,
  WidgetDataType.SEARCH_RESULTS,
  'newsarticle'
);

export default MyClientListSearchWidget;

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
    colCount: '1',
  },
  slots: {
    base: ['gap-6', 'grid', 'grid-cols-1'],
    card: ['flex', 'flex-col'],
    container: [
      'flex',
      'w-full',
      'h-full',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'bg-component-card-item-surface',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
    ],
    header: [
      'relative',
      'min-h-max',
      'w-full',
      'h-full md:h-auto',
      'overflow-hidden',
      'aspect-[1.61/1]',
      'text-color-fill-brand-1',
      'transition',
      'duration-300',
      'ease-in-out',
      'group',
      'pt-component-article-card-portrait-image-padding-top',
      'px-component-article-card-portrait-image-padding-x',
      'pb-component-article-card-portrait-image-padding-bottom',
    ],
    body: ['flex', 'flex-col', 'w-full', 'items-center'],
    contentWrapper: [
      'flex',
      'flex-col',
      'w-full',
      'gap-spacing-spacing-24',
      'py-component-card-item-landscape-content-1-col-padding-y',
      'px-component-card-item-landscape-content-1-col-padding-x',
    ],
    content: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-24'],

    headingWithEyebrow: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-4'],
    heading: [
      'font-typography-font-family-header',
      'font-normal',
      'text-typography-font-size-medium',
      'text-component-card-item-title',
      'leading-typography-line-height-medium',
    ],
    subheading: [
      'font-semibold',
      'font-typography-font-family-body',
      'text-typography-font-size-body-small',
      'leading-[150%]',
      'text-component-card-item-subtitle',
    ],
    description: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'text-typography-font-size-body-medium',
      'text-component-card-item-body',
      'leading-typography-line-height-body-medium',
      'mb-spacing-spacing-8',
    ],
    shortDescription: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'text-typography-font-size-body-medium',
      'text-component-card-item-body',
      'leading-typography-line-height-body-medium',
    ],
    sectionDivider: [
      'w-component-section-divider-width',
      'h-component-section-divider-height',
      'bg-component-section-divider',
    ],
    footer: [
      'flex',
      'flex-wrap',
      'flex-col',
      'gap-spacing-spacing-8',
      'items-start',
      'justify-center',
      'w-full',
    ],
    cta: [
      'flex',
      'gap-spacing-spacing-8',
      'items-center',
      'justify-center',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
      '!w-auto',
    ],
    imageWrapper: ['w-full', 'h-full', 'aspect-[1.61/1]', 'object-cover'],
  },
  variants: {
    colCount: {
      '1': {
        container: ['flex-col', 'md:flex-row', 'md:gap-0'],
        body: ['flex-row', 'items-start', 'text-left', 'lg:pl-0', 'justify-center'],
        description: [],
        content: ['text-left', 'justify-center'],
        contentWrapper: ['text-left', 'justify-center'],
        header: [
          'sm:py-component-article-card-landscape-image-padding-y',
          'sm:px-component-article-card-landscape-image-padding-x',
          'sm:pr-0',
        ],
        imageWrapper: [],
      },
    },
  },
});
